import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  HeartHandshake,
  Lock,
  MessageCircle,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import RelatedLinks from "@/components/common/RelatedLinks";
import { api, PROGRAM_COLORS, PROGRAM_LABELS } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import useSponsorStats from "@/hooks/useSponsorStats";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";
import SponsorCard from "@/components/sponsors/SponsorCard";
import SafetyFaq from "@/components/home/SafetyFaq";
import ActiveStep from "@/components/home/ActiveStep";
import ProductTour from "@/components/home/ProductTour";
import FounderNote from "@/components/home/FounderNote";
import TrustSafety from "@/components/home/TrustSafety";
import Testimonials from "@/components/home/Testimonials";
import { DISCLAIMER_TEXT } from "@/components/layout/Footer";

const PROGRAM_CARDS = [
  { key: "AA", blurb: "Twelve-step fellowship for alcohol recovery.", countLabel: "AA" },
  { key: "NA", blurb: "Twelve-step fellowship for recovery from drug addiction.", countLabel: "NA" },
  { key: "SMART", blurb: "Science-based, self-empowering recovery tools.", countLabel: "SMART Recovery" },
  { key: "CMA", blurb: "Twelve-step fellowship for crystal meth recovery.", countLabel: "CMA" },
  { key: "OTHER", blurb: "Any other recovery pathway that works for you.", countLabel: "other pathway" },
];

/** Truthful, friendly hero wording driven only by the live database count. */
function heroCountText(total) {
  if (!total) return "Be among the first sponsors in this community.";
  const noun = total === 1 ? "sponsor" : "sponsors";
  if (total < 10) {
    return `Join our growing community of ${total} ${noun} across AA, NA, SMART Recovery, CMA, and more.`;
  }
  return `${total} ${noun} available across AA, NA, SMART Recovery, CMA, and more.`;
}

/**
 * `count` may be undefined when the API response is missing that fellowship.
 * In that case we never invent a number - we show neutral browse wording.
 */
function programCountText(count, label) {
  if (typeof count !== "number") return `Browse ${label} sponsors`;
  if (count === 0) return "Be among the first sponsors in this community.";
  return `${count} ${label} ${count === 1 ? "sponsor" : "sponsors"} available`;
}

/**
 * The journey, in the four moves a person actually makes. Wording stays in the
 * language the recovery community already uses (sponsor, sponsee, fellowship,
 * lived experience) and promises nothing: a request may be declined, and
 * volunteers are not always available.
 */
const STEPS = [
  {
    icon: Search,
    title: "1. Browse",
    body: "Explore available peer sponsors. Browsing is free and needs no account - a first name or username is enough when you do sign up.",
  },
  {
    icon: Users,
    title: "2. Find someone who fits",
    body: "Read about their experience, recovery pathway and approach. Some sponsees want help working the Steps in a 12-step fellowship; others want accountability or simply someone who gets it.",
  },
  {
    icon: HeartHandshake,
    title: "3. Request a connection",
    body: "Reach out through Sponsor Me with a short note. They can accept or decline - no unwanted messages either way.",
  },
  {
    icon: MessageCircle,
    title: "4. Connect",
    body: "If the sponsor accepts, continue the conversation through the platform. Block and Report are in every conversation.",
  },
];

/**
 * Who this is for, said plainly. Deliberately framed as "if this is you" rather
 * than implying every visitor needs a sponsor, and nothing here suggests that
 * sponsorship guarantees recovery.
 */
const AUDIENCE = [
  "New to recovery and looking for support",
  "Looking for your first sponsor",
  "Wanting a sponsor whose approach fits you better",
  "Finding it hard to meet someone locally",
];

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);
  const { stats, statsState } = useSponsorStats();

  useEffect(() => {
    let active = true;
    api
      .get("/sponsors", { params: { page_size: 3, availability: "accepting" } })
      .then(({ data }) => {
        if (active) setFeatured(data.sponsors);
      })
      // Featured sponsors are decorative: a failure must not break the homepage,
      // but it is reported in development instead of being swallowed.
      .catch((err) => devWarn("Home.featuredSponsors", err));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="sm-hero">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-14 lg:py-24">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#E3DED4] text-xs font-semibold text-[#1F6F78]">
              <ShieldCheck className="w-3.5 h-3.5" /> Browsing is free forever. Sponsors never pay.
            </span>
            <p
              data-testid="hero-eyebrow"
              className="mt-4 text-xs sm:text-sm font-bold uppercase tracking-[0.14em] text-[#1F6F78]"
            >
              Peer sponsorship for people in recovery
            </p>
            <h1
              data-testid="hero-heading"
              className="mt-2 text-[1.75rem] sm:text-5xl lg:text-6xl font-bold text-[#1F2A2E] leading-[1.12] sm:leading-[1.05]"
            >
              Find a Peer Recovery Sponsor Who Gets It
            </h1>
            <p
              data-testid="hero-subheading"
              className="mt-3 sm:mt-5 text-base sm:text-lg text-[#4B5A60] leading-7 sm:leading-8 max-w-2xl"
            >
              Looking for a sponsor? Browse volunteer peer sponsors, learn about their recovery
              experience and approach, and request a connection with someone who may be a good fit.
            </p>
            {/* One-sentence definition, kept first and quotable (audit: g_definition). */}
            <p
              data-testid="home-definition"
              className="mt-4 text-[15px] sm:text-base text-[#1F2A2E] leading-7 max-w-2xl font-medium"
            >
              Sponsor Me is a peer-to-peer recovery platform that helps people find and connect
              with volunteer peer sponsors online.
            </p>
            {/* Primary mobile CTA. Kept directly after the headline block so it
                lands above the fold on common phone viewports; "Become a
                Sponsor" stays visibly secondary. Analytics unchanged - the
                existing hero_browse_sponsors_clicked / hero_become_sponsor_clicked
                events are reused, with no duplicates added. */}
            <div className="mt-5 sm:mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={() => {
                  trackOutcome(HEYCATCH_EVENTS.HERO_BROWSE_SPONSORS_CLICKED);
                  navigate("/sponsors");
                }}
                data-testid="find-sponsor-cta"
                className="w-full sm:w-auto bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white h-12 px-7 text-base justify-center"
              >
                Browse Sponsors <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  trackOutcome(HEYCATCH_EVENTS.HERO_BECOME_SPONSOR_CLICKED);
                  navigate("/become-a-sponsor");
                }}
                data-testid="become-sponsor-cta"
                className="w-full sm:w-auto h-12 px-7 text-base bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0] active:bg-[#C6E6E9] justify-center"
              >
                Become a Sponsor
              </Button>
            </div>
            {/* Honest quantity proof: the count line only appears when there
                is a real number to show. When the live total is zero we say so
                plainly in the early-stage panel below rather than displaying
                "0 sponsors" as though it were a trust metric. */}
            {!(statsState === "ready" && !stats.total) && (
            <p
              data-testid="hero-sponsor-count"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#4B5A60]"
              role="status"
              aria-live="polite"
            >
              <Users className="w-4 h-4 text-[#1F6F78] shrink-0" aria-hidden="true" />
              {statsState === "loading" ? (
                <span data-testid="hero-sponsor-count-loading">
                  <span className="sr-only">Loading the number of sponsors listed right now</span>
                  <span
                    aria-hidden="true"
                    className="sm-skeleton inline-block h-4 w-64 max-w-full rounded align-middle"
                  />
                </span>
              ) : statsState === "error" ? (
                <span data-testid="hero-sponsor-count-fallback">
                  Browse available peer sponsors
                  <Link
                    to="/sponsors"
                    data-testid="hero-sponsor-count-link"
                    className="ml-2 font-semibold text-[#1F6F78] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
                  >
                    Open the directory
                  </Link>
                </span>
              ) : (
                <span>
                  {heroCountText(stats.total)}{" "}
                  {stats.total > 0 && (
                    <Link
                      to="/sponsors"
                      data-testid="hero-sponsor-count-link"
                      className="font-semibold text-[#1F6F78] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
                    >
                      Browse them
                    </Link>
                  )}
                </span>
              )}
            </p>
            )}

            {/* Early-stage, zero-sponsor state. Real data only: this shows
                exactly when /api/sponsors/stats reports no browsable sponsor
                profiles, and disappears on its own the moment one exists. */}
            {statsState === "ready" && !stats.total && (
              <div
                data-testid="hero-early-stage"
                className="mt-5 rounded-2xl bg-white border border-[#E3DED4] p-5 max-w-2xl"
              >
                <p className="font-display text-base font-bold text-[#1F2A2E]">
                  Sponsor Me is a growing recovery community.
                </p>
                <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">
                  We're currently welcoming volunteer peer sponsors who want to give back and help
                  others connect. No sponsor profiles are listed yet - as soon as someone joins,
                  they appear in the directory.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={() => {
                      trackOutcome(HEYCATCH_EVENTS.BECOME_SPONSOR_EMPTY_STATE_CLICKED);
                      navigate("/become-a-sponsor");
                    }}
                    data-testid="early-stage-become-sponsor"
                    className="bg-[#1F6F78] hover:bg-[#185B62] text-white h-11 px-5"
                  >
                    Become a Sponsor
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      document
                        .getElementById("how-it-works")
                        ?.scrollIntoView({ behavior: "smooth", block: "start" })
                    }
                    data-testid="early-stage-how-it-works"
                    className="h-11 px-5 bg-white border-[#E3DED4] text-[#4B5A60] hover:bg-[#F2EFE8]"
                  >
                    Learn How Sponsor Me Works
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section
        data-testid="home-audience"
        aria-labelledby="audience-heading"
        className="bg-white border-y border-[#E3DED4]"
      >
        <div className="max-w-7xl mx-auto px-4 py-10 lg:py-12 grid gap-6 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <h2
              id="audience-heading"
              className="font-display text-xl sm:text-2xl font-bold text-[#1F2A2E]"
            >
              Who Sponsor Me is for
            </h2>
            <p data-testid="audience-copy" className="mt-3 text-[#4B5A60] leading-8">
              Whether you're new to recovery, looking for your first sponsor, or trying to find
              someone whose experience and approach fit you better, Sponsor Me gives you another way
              to make that connection. It is a peer sponsorship platform - sponsors are volunteers
              giving back, and you are never buying a sponsor.
            </p>
          </div>
          <ul data-testid="audience-list" className="grid gap-2.5 sm:grid-cols-2">
            {AUDIENCE.map((a) => (
              <li
                key={a}
                className="flex items-start gap-2.5 rounded-xl bg-[#FBF9F5] border border-[#E3DED4] px-3.5 py-3 text-sm text-[#1F2A2E] leading-6"
              >
                <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#1F6F78]" aria-hidden="true" />
                {a}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Programs */}
      <section className="max-w-7xl mx-auto px-4 py-12 lg:py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
          Choose your recovery pathway
        </h2>
        <p className="mt-2 text-[#4B5A60]">
          Sponsors list the fellowship or pathway they personally participate in.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {PROGRAM_CARDS.map((p) => (
            <Link
              key={p.key}
              to={`/sponsors?program=${p.key}`}
              data-testid={`program-card-${p.key}`}
              className="sm-card sm-card-hover p-5 bg-white flex flex-col gap-2"
            >
              <span
                className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-sm"
                style={{
                  backgroundColor: `${PROGRAM_COLORS[p.key]}1A`,
                  color: PROGRAM_COLORS[p.key],
                }}
              >
                {p.key === "OTHER" ? "+" : p.key}
              </span>
              <h3 className="font-display text-base font-bold text-[#1F2A2E]">
                {PROGRAM_LABELS[p.key]}
              </h3>
              <p className="text-sm text-[#4B5A60] leading-6">{p.blurb}</p>
              <p
                data-testid={`program-count-${p.key}`}
                className="text-sm font-semibold text-[#1F2A2E]"
                role="status"
                aria-live="polite"
              >
                {statsState === "loading" ? (
                  <>
                    <span className="sr-only">
                      Loading the number of {p.countLabel} sponsors
                    </span>
                    <span
                      aria-hidden="true"
                      className="sm-skeleton inline-block h-4 w-32 rounded align-middle"
                    />
                  </>
                ) : statsState === "error" ? (
                  `Browse ${p.countLabel} sponsors`
                ) : (
                  programCountText(stats.programs?.[p.key]?.total, p.countLabel)
                )}
              </p>
              <span className="mt-auto text-sm font-semibold text-[#1F6F78] inline-flex items-center gap-1">
                Browse <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Desired result / jobs-to-be-done */}
      <ActiveStep />

      {/* Product visualisation - real UI, illustrative data only */}
      <ProductTour />

      {/* How it works */}
      <section
        id="how-it-works"
        data-testid="home-how-it-works"
        aria-labelledby="how-it-works-heading"
        className="bg-white border-y border-[#E3DED4]"
      >
        <div className="max-w-7xl mx-auto px-4 py-14">
          <h2
            id="how-it-works-heading"
            className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
          >
            How It Works
          </h2>
          <p className="mt-2 text-[#4B5A60] leading-7 max-w-3xl">
            Four steps, at your pace. Nothing opens until a sponsor accepts.
          </p>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`how-it-works-step-${i + 1}`}
                className="relative rounded-2xl bg-[#F7F4EF] border border-[#E3DED4] p-5"
              >
                <span className="absolute top-4 right-4 font-display text-2xl font-bold text-[#DDD6C9]">
                  {i + 1}
                </span>
                <span className="w-10 h-10 rounded-xl bg-[#D7EEF0] text-[#1F6F78] flex items-center justify-center">
                  <s.icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <h3 className="mt-3 font-display text-base font-bold text-[#1F2A2E] pr-8">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <Button
              onClick={() => {
                trackOutcome(HEYCATCH_EVENTS.HOW_IT_WORKS_BROWSE_CLICKED);
                navigate("/sponsors");
              }}
              data-testid="how-it-works-browse-cta"
              className="bg-[#1F6F78] hover:bg-[#185B62] text-white h-11 px-6"
            >
              Browse Sponsors <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <p data-testid="how-it-works-no-guarantee" className="text-sm text-[#4B5A60] leading-6">
              Requests are not guaranteed to be accepted. Sponsors are volunteers, set their own
              availability, and may decline, pause their profile or not reply - so you may need to
              reach out to more than one person.
            </p>
          </div>
        </div>
      </section>

      {/* Founder-led trust: real photo, real words, links to the full story */}
      <FounderNote />

      {/* Real approved member quotes - renders nothing at all while empty */}
      <Testimonials />

      {/* Trust & Safety: only systems that actually exist */}
      <TrustSafety />

      {/* Featured sponsors */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-14">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]">
                Sponsors accepting sponsees
              </h2>
              <p className="mt-2 text-[#4B5A60]">Browsing profiles never costs anything.</p>
            </div>
            <Link to="/sponsors" className="text-sm font-semibold text-[#1F6F78] whitespace-nowrap">
              See all
            </Link>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((s) => (
              <SponsorCard key={s.user_id} sponsor={s} onMessage={() => navigate(`/sponsors/${s.user_id}`)} />
            ))}
          </div>
        </section>
      )}

      {/* Trust + pricing teaser */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="sm-card p-6 bg-white">
            <Lock className="w-6 h-6 text-[#1F6F78]" />
            <h3 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">Privacy comes first</h3>
            <p className="mt-2 text-sm text-[#4B5A60] leading-6">
              We never ask for your full legal name, exact address, employer, sobriety date or
              medical history. Photos are optional and phone numbers are never shown publicly.
            </p>
          </div>
          <div className="sm-card p-6 bg-white">
            <ShieldCheck className="w-6 h-6 text-[#1F6F78]" />
            <h3 className="mt-3 font-display text-lg font-bold text-[#1F2A2E]">Safety tools always free</h3>
            <p className="mt-2 text-sm text-[#4B5A60] leading-6">
              Block and report are available in every conversation and never require a membership.
              Emergency and crisis resources stay free for everyone.
            </p>
          </div>
          <div className="rounded-2xl p-6 bg-[#1F2A2E] text-[#F7F4EF]">
            <h3 className="font-display text-lg font-bold">20 free messages to start</h3>
            <p className="mt-2 text-sm leading-6 text-[#CBD6D8]">
              Every member looking for a sponsor gets 20 free sent messages. After that, a plan for
              unlimited messaging is $1.99 a week, $4.99 a month or $29.99 a year - it renews
              automatically until you cancel, and you can cancel any time. Sponsors message for
              free, forever.
            </p>
            <Button
              onClick={() => navigate("/pricing")}
              data-testid="home-pricing-cta"
              className="h-11 touch-manipulation mt-4 bg-[#D07A4A] hover:bg-[#B96536] text-white"
            >
              See pricing
            </Button>
          </div>
        </div>

        <div className="mt-6 rounded-2xl bg-[#F2EFE8] border border-[#E3DED4] p-5">
          <h4 className="font-display text-base font-bold text-[#1F2A2E]">
            Independent Recovery Community
          </h4>
          <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">{DISCLAIMER_TEXT}</p>
        </div>
      </section>

      {/* Sponsor safety & FAQ */}
      <SafetyFaq />
      <RelatedLinks
        heading="Explore Sponsor Me"
        links={[
          { to: "/sponsors", label: "Find a recovery sponsor" },
          { to: "/become-a-sponsor", label: "Become a sponsor" },
          { to: "/pricing", label: "See pricing and plans" },
          { to: "/about", label: "Read our story" },
          { to: "/vs/ai-sponsor", label: "Human sponsors vs AI recovery apps" },
        ]}
      />
    </div>
  );
}
